import {
    getChartSeriesConstructorByType,
} from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/series/series-factory';
import { DataLabels } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/properties/data-label';
import type { DeepReadonly } from 'ts-essentials';
import type {
    ICTCatAx, ICTValAx, ICTDateAx, ISubChartData,
    IEGAxis, ISubChartSerData, IStockChartData,
    ISurface3DChartData, ISurfaceChartData, ICTDLbls,
    DeltaOf,
} from '@tencent/slide-sdk-model';
import type {
    IChartAxis,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import type { ISubChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart';
import type { SerRules } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/series';
import {
    AxisConverter, axisTypeMap,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/chart/plot-area/axis-converter';

type WithVaryColorsChart = Exclude<ISubChartData, IStockChartData | ISurfaceChartData | ISurface3DChartData>;

export abstract class SubChartConverter {
    public static type; // 给getChartSeriesConstructor使用
    public static convertSubChartDataSdkToUi(
        sdkData: ISubChartData,
        uiData: ISubChartModel,
    ) {
        if (sdkData.type !== undefined) {
            uiData.originType = sdkData.type;
        }

        if (sdkData.axesAddIndex !== undefined) {
            uiData.axesAddIndex = sdkData.axesAddIndex;
        }

        if (this.isInnerValNotNil((sdkData as WithVaryColorsChart).varyColors)) {
            uiData.varyColors = (sdkData as WithVaryColorsChart).varyColors?.val as boolean;
        }

        if (sdkData.axes !== undefined) {
            const egAxisKeys = ['valAx', 'catAx', 'dateAx', 'serAx'] as Array<keyof IEGAxis>;
            uiData.axes = sdkData.axes.map((egAxis) => {
                // 理论上不会出现egAxis里面没有东西的情况，因为坐标轴是全量替换的不会走delta，先用valAx做个兜底
                // TODO 这里需要优化一下，应该有更好的写法
                const axisType = egAxisKeys.find(key => egAxis[key] !== undefined) || 'valAx';
                const sdkAxisData = egAxis[axisType];
                const uiAxisData: IChartAxis = {
                    type: axisTypeMap[axisType],
                };

                AxisConverter.convertAxisDataSdkToUi(
                    sdkAxisData as DeepReadonly<ICTCatAx | ICTDateAx | ICTValAx>,
                    uiAxisData,
                );
                return uiAxisData;
            });

            uiData.axisIds = sdkData.axes?.map((axis) => {
                const axisType = egAxisKeys.find(key => axis[key] !== undefined);
                // 理论上不会出现egAxis里面没有东西的情况，因为坐标轴是全量替换的不会走delta
                // 但是因为这里的类型可为undefined，就用0做一下兜底
                // TODO 这里需要优化一下，应该有更好的写法
                return axis[axisType!]?.axId?.val || 0;
            });
        }

        if (sdkData.ser !== undefined) {
            const ChartSeries = getChartSeriesConstructorByType(uiData.type);
            if (ChartSeries) {
                uiData.serRules = this.generateSerRules(sdkData.ser);
                uiData.series = sdkData.ser?.map(sdkSerData => new ChartSeries(
                    sdkSerData as ISubChartSerData,
                    uiData.serRules,
                )) || [];
            } else {
                uiData.series = [];
            }
        }

        if (sdkData.dLbls !== undefined && sdkData.dLbls !== null) {
            uiData.dataLabels = new DataLabels(sdkData.dLbls as ICTDLbls);
        }
    }

    public static convertSubChartDataUiToSdk(
        uiData: ISubChartModel,
        sdkData: DeltaOf<ISubChartData>,
    ) {
        if (uiData.originType !== undefined) {
            (sdkData.type as any) = uiData.originType;
        }

        if (uiData.axesAddIndex !== undefined) {
            sdkData.axesAddIndex = uiData.axesAddIndex;
        }

        if (uiData.varyColors !== undefined) {
            (sdkData as WithVaryColorsChart).varyColors = { val: uiData.varyColors };
        }

        if (uiData.axes !== undefined) {
            sdkData.axes = uiData.axes.map((axis) => {
                if (!axis) {
                    return axis;
                }

                const { type } = axis;
                const sdkAxisData = {};
                AxisConverter.convertAxisDataUiToSdk(axis, sdkAxisData);

                const egAxis: IEGAxis = {};
                if (type === axisTypeMap.catAx) {
                    egAxis.catAx = sdkAxisData;
                } else if (type === axisTypeMap.valAx) {
                    egAxis.valAx = sdkAxisData;
                } else if (type === axisTypeMap.dateAx) {
                    egAxis.dateAx = sdkAxisData;
                }

                return egAxis;
            });
        }

        if (uiData.axisIds !== undefined) {
            // @ts-ignore
            sdkData.axId = uiData.axisIds.map((id) => {
                if (!id) {
                    return id;
                }

                return ({ val: id });
            });
        }

        if (uiData.series !== undefined) {
            if (!uiData.series) {
                (sdkData.ser as any) = null;
            } else {
                sdkData.ser = uiData.series.map((serItem) => {
                    if (serItem) {
                        const ChartSeries = getChartSeriesConstructorByType(this.type);
                        return ChartSeries?.convertDataUiToSdk(serItem);
                    }

                    return serItem;
                });
            }
        }

        if (uiData.dataLabels !== undefined) {
            if (!uiData.dataLabels) {
                sdkData.dLbls = uiData.dataLabels;
            } else {
                sdkData.dLbls = DataLabels.convertDataUiToSdk(uiData.dataLabels);
            }
        }
    }

    public static generateSerRules(seriesList): SerRules {
        return {
            count: this.getDataListCount(seriesList),
            categoryPointList: this.getCategoryPointList(seriesList),
        };
    }

    // 计算 subChart中ser.dataList的长度（subChart中每一个ser.dataList的长度相同，长度为ser中values.count 最大的一个）
    public static getDataListCount(seriesList) {
        if (!seriesList) {
            return 0;
        }

        return Math.max(...seriesList.map(series => (series.disable ? 0 : (series.valueCount || 0))));
    }

    // 计算坐标轴展示的分类列表
    // 前端最终的categoryList（坐标轴展示的分类列表），以第一个有ser.categories的数据为准，后面的数据的category取值，都通过索引，去取这个categoryList，
    // 没有对应索引就为空，不管他自己的categories中是不是有对应的category
    public static getCategoryPointList(seriesList) {
        const baseCategorySeries = seriesList?.find(ser => ser
            .categories.some(categoryItem => categoryItem.categoryPoint));

        return baseCategorySeries?.categories?.map(categoryItem => categoryItem.categoryPoint) || [];
    }

    protected static isInnerValNotNil<ExpectType>(obj?: Partial<ComplexValType<ExpectType | undefined | null> | null>):
        obj is ComplexValType<ExpectType> {
        return obj?.val !== undefined && obj?.val !== null;
    }

    protected static convertComplexValueUiToSdk<U, S>(
        uiData: U, uiDatakey: keyof U,
        sdkData: S, sdkDataKey: keyof S,
    ) {
        if (uiData[uiDatakey] !== undefined) {
            if (uiData[uiDatakey] === null) {
                (sdkData[sdkDataKey] as any) = null;
            } else {
                (sdkData[sdkDataKey] as any) = { val: uiData[uiDatakey] };
            }
        }
    }
}

type ComplexValType<T> = { val: T };
